import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import Loading from '../../../components/common/loading';
import SelectInput from '../../../components/common/inputs/selectInput.jsx';
import ToggleInput from '../../../components/common/inputs/toggleInput.jsx';
import NotificationManager from '../../../utils/notificationManager';

const LINK_SPEED_OPTIONS = [
  { label: '10 Mbps only', value: '10' },
  { label: '100 Mbps only', value: '100' },
  { label: '10 & 100 Mbps (both)', value: 'both' },
];

const LINK_DUPLEX_OPTIONS = [
  { label: 'Half-duplex only', value: 'half' },
  { label: 'Full-duplex only', value: 'full' },
  { label: 'Half & Full duplex (both)', value: 'both' },
];

const styles = {
  wrapper: {
    background: 'var(--app-surface-1)',
    borderRadius: 8,
    border: '1px solid var(--app-border)',
    boxShadow: 'none',
    padding: '24px',
  },
  sectionGrid: {
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: 'minmax(280px, 420px) minmax(220px, 1fr)',
  },
  sectionStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sectionTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--app-text)',
  },
  sectionDescription: {
    marginTop: 6,
    color: 'var(--app-text-muted)',
    fontSize: '13px',
  },
  formCard: {
    background: 'var(--app-surface-2)',
    borderRadius: 8,
    padding: '20px',
  },
  statusCard: {
    background: 'var(--app-surface-2)',
    borderRadius: 8,
    padding: '20px',
  },
  loadingState: {
    minHeight: '160px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    color: 'var(--app-text-muted)',
    fontSize: '14px',
    fontWeight: 500,
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '12px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  note: {
    marginTop: '8px',
    color: 'var(--warning-color)',
    fontSize: '13px',
  },
  banner: {
    marginBottom: '16px',
    padding: '12px 16px',
    borderRadius: 6,
    fontSize: '13px',
  },
  warningBanner: {
    background: 'var(--warning-background)',
    color: 'var(--warning-color)',
  },
  infoBanner: {
    background: 'var(--app-surface-3)',
    color: 'var(--app-text-muted)',
    borderColor: 'var(--app-border)',
  },
  statusList: {
    display: 'grid',
    gap: '12px',
  },
  statusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: 'var(--app-text)',
  },
  statusLabel: {
    fontWeight: 600,
    color: 'var(--app-text-muted)',
  },
  errorText: {
    color: 'var(--danger-color)',
    fontSize: '13px',
  },
};

const normalizeBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  }
  return Boolean(value);
};

const defaultValues = {
  linkSpeed: '',
  linkDuplex: '',
  linkAutoneg: true,
};

export default function DeviceConfigurationEthernetLink({
  section,
  device,
  conf,
  confLoading,
  refreshConf,
  user,
  deviceOffline,
  deviceLoaded,
}) {
  const serial = device?.enclosureSerialNumber;
  const [formValues, setFormValues] = useState(defaultValues);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit = (user?.role?.editDeviceInfo ?? true) && !deviceOffline;
  const deviceInfoLoading = deviceLoaded === false && !device?.modelNumber;
  const isDxDevice = Boolean(device?.modelNumber?.startsWith('NP'));

  const initialValues = useMemo(
    () => ({
      linkSpeed: conf?.linkSpeed ?? '',
      linkDuplex: conf?.linkDuplex ?? '',
      linkAutoneg: normalizeBoolean(conf?.linkAutoneg),
    }),
    [conf]
  );

  useEffect(() => {
    setFormValues(initialValues);
    setError('');
  }, [initialValues]);

  const handleChange = useCallback((field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError('');
  }, []);

  const isDirty = useMemo(() => {
    return (
      formValues.linkSpeed !== initialValues.linkSpeed ||
      formValues.linkDuplex !== initialValues.linkDuplex ||
      normalizeBoolean(formValues.linkAutoneg) !== normalizeBoolean(initialValues.linkAutoneg)
    );
  }, [formValues, initialValues]);

  const handleSubmit = useCallback(async () => {
    if (!canEdit || !serial || saving) {
      return;
    }
    if (!isDirty) {
      setError('No changes detected for this section.');
      return;
    }

    const payload = {};
    if (formValues.linkSpeed !== initialValues.linkSpeed) {
      payload.linkSpeed = formValues.linkSpeed || undefined;
    }
    if (formValues.linkDuplex !== initialValues.linkDuplex) {
      payload.linkDuplex = formValues.linkDuplex || undefined;
    }
    if (
      normalizeBoolean(formValues.linkAutoneg) !==
      normalizeBoolean(initialValues.linkAutoneg)
    ) {
      payload.linkAutoneg = normalizeBoolean(formValues.linkAutoneg);
    }

    if (!Object.keys(payload).length) {
      setError('No changes detected for this section.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      await axios.put(`/v1/conf/${serial}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      NotificationManager.success('Ethernet link settings updated.');
      if (refreshConf) {
        refreshConf({ useCache: false, force: true });
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        'Failed to update ethernet link configuration. Please try again.';
      setError(message);
      NotificationManager.alert(message);
    } finally {
      setSaving(false);
    }
  }, [canEdit, formValues, initialValues, refreshConf, saving, serial]);

  const renderLoadingState = () => (
    <div style={styles.wrapper}>
      <div style={styles.loadingState} role="status" aria-live="polite">
        <Loading size="24px" black />
        <span>Loading Ethernet link configuration...</span>
      </div>
    </div>
  );

  if (deviceInfoLoading) {
    return renderLoadingState();
  }

  if (!isDxDevice) {
    return (
      <div style={{ ...styles.wrapper, ...styles.infoBanner }}>
        Ethernet link configuration is only available on DX-series devices.
      </div>
    );
  }

  if (confLoading) {
    return renderLoadingState();
  }

  if (!conf) {
    return (
      <div style={styles.wrapper}>
        Link settings will appear once configuration data finishes loading.
      </div>
    );
  }

  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: 'var(--app-text)' }}>
          {section?.label || 'Ethernet Link'}
        </h4>
        <p style={{ marginTop: '8px', color: 'var(--app-text-muted)', fontSize: '14px' }}>
          {section?.description ||
            'Configure advertised link speed, duplex, and negotiation behaviour.'}
        </p>
      </div>
    <div style={styles.wrapper}>
      {deviceOffline && (
        <div style={{ ...styles.banner, ...styles.warningBanner }}>
          Device is offline. Configuration changes are disabled until it reconnects.
        </div>
      )}
      {!deviceOffline && canEdit === false && (
        <div style={{ ...styles.banner, ...styles.warningBanner }}>
          You can view this configuration, but your role does not allow editing device settings.
        </div>
      )}
      <div style={styles.sectionGrid}>
        <div style={styles.sectionStack}>
          <div>
            <h5 style={styles.sectionTitle}>Advertised Link Settings</h5>
            <p style={styles.sectionDescription}>
              Configure the link speed, duplex, and negotiation behaviour advertised on the copper
              interface.
            </p>
          </div>
          <div style={styles.formCard}>
            <div style={styles.fieldGroup}>
              <SelectInput
                label="Link Speed"
                value={formValues.linkSpeed}
                options={LINK_SPEED_OPTIONS}
                callback={(value) => handleChange('linkSpeed', value)}
                disabled={!canEdit}
              />
              <SelectInput
                label="Link Duplex"
                value={formValues.linkDuplex}
                options={LINK_DUPLEX_OPTIONS}
                callback={(value) => handleChange('linkDuplex', value)}
                disabled={!canEdit}
              />
              <ToggleInput
                label="Auto-Negotiation"
                value={normalizeBoolean(formValues.linkAutoneg)}
                callback={(value) => handleChange('linkAutoneg', value)}
                disabled={!canEdit}
                toggleLabelOn="Enabled"
                toggleLabelOff="Disabled"
              />
            </div>
            <div style={styles.note}>
              WARNING: Changing link settings will reboot the ethernet interface briefly. You may
              lose connectivity until the link renegotiates.
            </div>
            <div style={styles.actions}>
              <button
                type="button"
                className="button"
                onClick={handleSubmit}
                disabled={!canEdit || !isDirty || saving || !serial}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button
                type="button"
                className="button secondary"
                onClick={() => setFormValues(initialValues)}
                disabled={!canEdit || saving || !isDirty}
              >
                Reset
              </button>
              {error ? <span style={styles.errorText}>{error}</span> : null}
            </div>
          </div>
        </div>
        <div style={styles.sectionStack}>
          <div>
            <h5 style={styles.sectionTitle}>Current Link Status</h5>
            <p style={styles.sectionDescription}>
              Values reported directly by the active ethernet interface.
            </p>
          </div>
          <div style={styles.statusCard}>
            <div style={styles.statusList}>
              <div style={styles.statusRow}>
                <span style={styles.statusLabel}>Actual Link Speed</span>
                <span>{conf?.linkSpeedActual || '—'}</span>
              </div>
              <div style={styles.statusRow}>
                <span style={styles.statusLabel}>Actual Duplex</span>
                <span>{conf?.linkDuplexActual || '—'}</span>
              </div>
              <div style={styles.statusRow}>
                <span style={styles.statusLabel}>Auto-Negotiation</span>
                <span>{normalizeBoolean(conf?.linkAutonegActual) ? 'Enabled' : 'Disabled'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
